import { DialogContent, TextField } from '@mui/material';
import FormDialogContainer from '../../FormDialogContainer/FormDialogContainer';
import { DialogActionButtons } from '../../DialogActionButtons/DialogActionButtons';
import { useTranslation } from 'react-i18next';
import { candidateJobStatusOptions } from '../../../Types/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { StatusSelect } from '../../StatusSelect/StatusSelect';
import { useCandidateJobSubStatus } from '../../../Hooks/useCandidateJobSubStatus';
import { updateCandidateJobStatus } from '../../../Services/CandidateJobs';
import { useParams } from 'react-router-dom';
import {useJobStore} from "../../../Stores/useGeneralAppStore";

interface Props {
  candidateJob: CandidateJob;
  open: boolean;
  handleClose: () => void;
}

export const CandidateJobStatusDialog = ({ candidateJob, open, handleClose }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { candidateId } = useParams();

  const [status, setStatus] = useState(candidateJob.generalData.status);
  const [subStatus, setSubStatus] = useState(candidateJob.generalData.subStatus);
  const [comment, setComment] = useState(candidateJob.generalData.comment || '');
  const { setSelectedStoreJobId } = useJobStore();

  const { candidateJobSubStatusOptions } = useCandidateJobSubStatus(
    status,
    setSubStatus,
    subStatus
  );

  const subStatusOptions = candidateJobSubStatusOptions.filter((option) => option.value !== 'all');

  const title = t('candidate_jobs.status.update.title');
  const successMessage = t('jobs.status.update.success_message');

  const resetForm = () => {
    setStatus(candidateJob.generalData.status);
    setSubStatus(candidateJob.generalData.subStatus);
    setComment(candidateJob.generalData.comment || '');
  };

  const handleCloseAndReset = () => {
    resetForm();
    handleClose();
  };

  const handleStatusChange = (val: string) => {
    setStatus(val);
    setSubStatus('');
  };

  const handleSubStatusChange = (val: string) => {
    setSubStatus(val);
  };

  useEffect(() => {
    setComment(candidateJob.generalData.comment || '');
  }, [candidateJob]);

  const { mutate: updateCandidateJobStatusMutation } = useMutation({
    mutationFn: () =>
      updateCandidateJobStatus(`${candidateJob.id}`, {
        status,
        subStatus: subStatus === 'all' ? '' : subStatus,
        comment: comment.trim() || undefined,
      }),
    onError: (error: { response: { data: string } }) => {
      toast.error(error?.response?.data);
    },
    onSuccess: () => {
      setSelectedStoreJobId(candidateJob.id);
      toast.success(successMessage);
      handleCloseAndReset();
      queryClient.invalidateQueries({ queryKey: ['getCandidateJobs', candidateId] });
    },
  });

  const handleConfirm = () => {
    updateCandidateJobStatusMutation();
  };

  return (
    <FormDialogContainer open={open} handleClose={handleCloseAndReset} title={title}>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <StatusSelect
          handleChange={handleStatusChange}
          status={status}
          options={candidateJobStatusOptions.filter((option) => option.value !== 'all')}
        />

        <StatusSelect
          disabled={subStatusOptions.length === 0}
          handleChange={handleSubStatusChange}
          status={subStatus}
          options={subStatusOptions}
        />

        <TextField
          multiline
          minRows={3}
          label={t('candidate_jobs.status.update.comment')}
          placeholder={t('candidate_jobs.status.update.comment_placeholder')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{ marginLeft: 1.5 }}
        />
      </DialogContent>
      <DialogActionButtons onCancel={handleCloseAndReset} onConfirm={handleConfirm} />
    </FormDialogContainer>
  );
};
